import { create } from 'zustand';
import { UserInterface } from '../../models/user.model';
import {
    forgotPassword,
    loginWithToken,
    logout,
    signIn,
    verifyForgotPassword,
} from '../../api/auth';
import toastifyInstance from '../../utils/toastify';
import {
    checkDeviceId,
    removeLocalStorage,
    setLocalStorage,
} from '../../utils/localStorage';
import { UserRole } from '../../constants/user';
import {
    REFRESH_TOKEN_KEY,
    JWT_TOKEN_KEY,
    USER_DATA_KEY,
} from '../../constants/auth';
import {
    removeSessionStorage,
    setSessionStorage,
} from '../../utils/sessionStorage';
import dayjs from 'dayjs';

interface OTP {
    timeEx: Date | null;
    email: string | null;
    openForm: boolean;
}

export interface AuthState {
    user: UserInterface | null;
    loading: boolean;
    otp: OTP | null;
    setUser?: (user: UserInterface) => void;
    signIn: (email: string, password: string) => Promise<void>;
    setOTP: (otp: OTP | null) => void;
    loginWithToken: () => Promise<void>;
    forgotPassword: (email: string) => Promise<void>;
    verifyForgotPassword: (
        email: string,
        otp: string,
        password: string
    ) => Promise<void>;
    logout: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
    user: null,
    loading: false,
    otp: null,
    signIn: async (email: string, password: string) => {
        try {
            set({ loading: true });
            const response = await signIn({
                email,
                password,
                deviceId: checkDeviceId(),
            });

            if (response.user && (response.user.role === UserRole.ADMIN || response.user.role === UserRole.MANAGER)) {
                set({ user: response.user });
                setLocalStorage(JWT_TOKEN_KEY, response.accessToken);
                setLocalStorage(REFRESH_TOKEN_KEY, response.refreshToken);
                setSessionStorage(USER_DATA_KEY, response.user);
                toastifyInstance.success('Access granted!');
                window.location.reload();
            } else {
                toastifyInstance.error(
                    'Something wrong!'
                );
            }
        } catch (error) {
            console.error(error);
            toastifyInstance.error('Something wrong!');
        } finally {
            set({ loading: false });
        }
    },
    setOTP: (otp: OTP | null) => {
        set({ otp });
    },

    setUser: (user: UserInterface) => {
        set({ user });
        setSessionStorage(USER_DATA_KEY, user);
    },

    loginWithToken: async () => {
        try {
            set({ loading: true });
            const response = await loginWithToken();
            if (response.user && (response.user.role === UserRole.ADMIN || response.user.role === UserRole.MANAGER)) {
                set({ user: response.user });
                setSessionStorage(USER_DATA_KEY, response.user);
            } else {
                set({ user: null });
                removeSessionStorage(USER_DATA_KEY);
            }
        } catch (error) {
            console.error(error);
        } finally {
            set({ loading: false });
        }
    },

    forgotPassword: async (email: string) => {
        try {
            set({ loading: true });
            const response = await forgotPassword(email);

            set({
                otp: {
                    email: response.email,
                    timeEx: dayjs(response.timeEx).add(3, 'minutes').toDate(),
                    openForm: true,
                },
            });
            toastifyInstance.success('OTP sent successfully');
        } catch (error) {
            console.error(error);
            toastifyInstance.error('Failed to send OTP');
        } finally {
            set({ loading: false });
        }
    },

    verifyForgotPassword: async (
        email: string,
        otp: string,
        password: string
    ) => {
        try {
            set({ loading: true });
            await verifyForgotPassword(email, otp, password);
            toastifyInstance.success('OTP verified successfully');
            set({ otp: null });
        } catch (error) {
            console.error(error);
            toastifyInstance.error('OTP verification failed');
        } finally {
            set({ loading: false });
        }
    },

    logout: async () => {
        try {
            const response = await logout();
            if (response?.status === 201) {
                removeLocalStorage(JWT_TOKEN_KEY);
                removeLocalStorage(REFRESH_TOKEN_KEY);
                removeSessionStorage(USER_DATA_KEY);
                set({ user: null });
                window.location.href = '/login';
            }
        } catch (error) {
            console.error(error);
        }
    },
}));
